/**
 * 子ども医療助成の判定ロジック。
 *
 * 当院（てのひら眼科）は東京都に所在するため、子ども医療証（マル子）の扱いが
 * 在住地によって異なる:
 * - 東京都内在住    : 子ども医療証を利用できる → 予約フローで医療証の登録/確認を行う
 * - 東京都外在住    : 当院では利用できず一時自己負担 + 還付申請が必要 → 同意を取得する
 *
 * 対象は「年齢条件を満たす保険診療の患者」。患者の登録住所（prefecture）で都内/都外を判定する。
 * patient（予約フロー）/ clinic（診療詳細表示）双方で再利用するため shared_front に置く。
 *
 * `domain/index.ts` で `export * as Consultation` 経由で公開されるため、
 * 利用側からは `Consultation.classifyChildMedicalSubsidyFlow(...)` のように参照される。
 */
import { isSelfPayMenu } from "./menuId";
/** 子ども医療助成の在住判定に用いる都道府県（当院所在地）。 */
export const CHILD_MEDICAL_SUBSIDY_PREFECTURE = "東京都";
/**
 * 予約フローで取りうる子ども医療助成の分岐。
 */
export const ChildMedicalSubsidyFlow = {
    /** 対象外（年齢条件を満たさない、または保険診療でない）。追加画面なし。 */
    None: "none",
    /** 東京都外在住: 注意書き＋同意を取得する（予約フロー S3）。 */
    OutsideTokyoConsent: "outsideTokyoConsent",
    /** 東京都内在住: 子ども医療証の登録/確認を行う（予約フロー S11）。 */
    TokyoMedicalCard: "tokyoMedicalCard",
};
/**
 * 子ども医療助成の年齢条件を満たすか判定する。
 *
 * 条件: 「18 歳に達した日以降の最初の 3 月 31 日（高校 3 年生の年度末）」まで対象。
 * 例) 2007-04-01 生まれ → 18 歳の誕生日 2025-04-01 → 対象は 2026-03-31 まで
 *     2007-03-31 生まれ → 18 歳の誕生日 2025-03-31 → 対象は 2025-03-31 まで
 *
 * birthDay が未設定 / 不正な日付の場合は false を返す。
 */
export const isChildMedicalSubsidyAgeEligible = (birthDay, now = new Date()) => {
    if (!birthDay)
        return false;
    const birth = new Date(birthDay);
    if (Number.isNaN(birth.getTime()))
        return false;
    // 18 歳に達する年
    const eighteenthYear = birth.getFullYear() + 18;
    // 18 歳の誕生日が 3/31 以前（1〜3 月生まれ）ならその年度末、4 月以降なら翌年の年度末。
    const isBornBeforeApril = birth.getMonth() <= 2; // 0=1月, 2=3月
    const cutoffYear = isBornBeforeApril ? eighteenthYear : eighteenthYear + 1;
    // 年度末 3/31 の終わり（その日いっぱいを対象に含める）
    const cutoff = new Date(cutoffYear, 2, 31, 23, 59, 59, 999);
    return now.getTime() <= cutoff.getTime();
};
/**
 * 東京都内在住か判定する。
 */
export const isTokyoResident = (prefecture) => prefecture === CHILD_MEDICAL_SUBSIDY_PREFECTURE;
/**
 * 子ども医療助成の予約フロー分岐を判定する。
 *
 * 保険診療かつ年齢条件を満たす場合に、在住地で以下を返す:
 * - 東京都外 → `OutsideTokyoConsent`（同意 S3）
 * - 東京都内 → `TokyoMedicalCard`（医療証 S11）
 * それ以外は `None`。
 */
export const classifyChildMedicalSubsidyFlow = ({ menuId, prefecture, birthDay, now, }) => {
    // 保険診療のみ対象（自由診療・Upneeq は対象外）。undefined は保険診療相当として扱う。
    if (isSelfPayMenu(menuId))
        return ChildMedicalSubsidyFlow.None;
    if (!isChildMedicalSubsidyAgeEligible(birthDay, now)) {
        return ChildMedicalSubsidyFlow.None;
    }
    return isTokyoResident(prefecture)
        ? ChildMedicalSubsidyFlow.TokyoMedicalCard
        : ChildMedicalSubsidyFlow.OutsideTokyoConsent;
};
/**
 * 子ども医療助成の対象患者か（在住地を問わず、年齢条件 & 保険診療）。
 * clinic の診療詳細での「対象であれば表示」判定に利用する。
 */
export const isChildMedicalSubsidyTarget = (input) => classifyChildMedicalSubsidyFlow(input) !== ChildMedicalSubsidyFlow.None;
